import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import { OnboardingLayout } from "@layouts/OnboardingLayout";
import { InputComponent } from "@shared/forms";
import { PrimaryButton } from "@shared/buttons";
import { ErrorDisplay, SuccessDisplay } from "@shared/notifications";

// import google from '../../assets/icons/google.svg';

import { register, verifyAean, verifyNiesv } from "@services/krent.service";
import Logo from "@shared/Logo";
import { SelectDropdown } from "@shared/forms/SelectComponent";

export const SignUp = () => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [userNameError, setUserNameError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [togglePassword, setTogglePassword] = useState(false);
  const [referral, setReferral] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [isAssociationMember, setIsAssociationMember] = useState("");
  const [estateNumber, setEstateNumber] = useState("");
  const [niesvError, setNiesvError] = useState("");
  const [niesvVerified, setNiesvVerified] = useState(false);
  const [aeanError, setAeanError] = useState("");
  const [aeanVerified, setAeanVerified] = useState(false);

  const navigate = useNavigate();
  const { type } = useParams();

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(e.target.value);
    if (e.target.value !== "yes") {
      setRegNumber("");
      setNiesvError("");
      setNiesvVerified(false);
    }
  };

  const handleAssociationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setIsAssociationMember(e.target.value);
    if (e.target.value !== "yes") {
      setEstateNumber("");
      setAeanError("");
      setAeanVerified(false);
    }
  };
  const validateNiesv = async () => {
    try {
      const niesvNumber = regNumber;
      await verifyNiesv(niesvNumber);
      setNiesvVerified(true);
      setNiesvError("");
      return true;
    } catch (err) {
      setNiesvVerified(false);
      setNiesvError(typeof err === "string" ? err : "Invalid NIESV number");
      return false;
    }
  };

  const validateAean = async () => {
    try {
      const aeanNumber = estateNumber;
      await verifyAean(aeanNumber);
      setAeanVerified(true);
      setAeanError("");
      return true;
    } catch (err) {
      setAeanVerified(false);
      setAeanError(typeof err === "string" ? err : "Invalid AEAN number");
      return false;
    }
  };


  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNiesvError("");
    setAeanError("");

    try {
      // Basic validation for all account types
      if (!firstname.trim()) {
        setError("Please enter your first name");
        setLoading(false);
        return;
      }
      
      if (!lastname.trim()) {
        setError("Please enter your last name");
        setLoading(false);
        return;
      }
      
      if (!email.trim()) {
        setError("Please enter your email address");
        setLoading(false);
        return;
      }
      
      if (!phoneNumber.trim()) {
        setError("Please enter your phone number");
        setLoading(false);
        return;
      }
      
      if (!password.trim()) {
        setError("Please create a password");
        setLoading(false);
        return;
      }
      
      if (password.length < 8) {
        setError("Password must be at least 8 characters long");
        setLoading(false);
        return;
      }
      
      if (!preferredName.trim()) {
        setError("Please choose a username");
        setLoading(false);
        return;
      }

      if (type !== "developer" && !agree) {
        setError("Please accept the terms and conditions to continue");
        setLoading(false);
        return;
      }

      if (preferredName === firstname || preferredName === lastname) {
        setUserNameError(
          "Username must be different from your first and last name"
        );
        setLoading(false);
        return;
      }

      if (type === "agent") {
        if (selectedOption === "yes") {
          const isNiesvValid = await validateNiesv();
          if (!isNiesvValid) {
            setLoading(false);
            return;
          }
        }

        if (isAssociationMember === "yes") {
          const isAeanValid = await validateAean();
          if (!isAeanValid) {
            setLoading(false);
            return;
          }
        }
      }

      const data = {
        firstname,
        lastname,
        email,
        phone: phoneNumber,
        password,
        passwordConfirm: password,
        preferredName,
        niesvNumber:
          type === "agent" && selectedOption === "yes" ? regNumber : undefined,
        aeanNumber:
          type === "agent" && isAssociationMember === "yes"
            ? estateNumber
            : undefined,
        role:
          type === "buyer"
            ? "user"
            : type === "developer"
              ? "developer"
              : type === "seller"
                ? "landlord"
                : type === "property-manager"
                  ? "property-manager"
                  : "agent",
        socialMediaSource: referral,
      };

      if (type === "developer") {
        // Store the registration data in localStorage for the next step
        console.log("Storing developer data:", data);
        localStorage.setItem("krent-signup-developer-data", JSON.stringify(data));
        navigate("/get-started/developer-upload");
      } else {
        const response = await register(data);
        setSuccess(response.message);
        navigate("/redirect", { state: { firstname, lastname, email } });
      }
    } catch (err: any) {
      console.log("Registration error:", err);
      if (err?.errors && Array.isArray(err.errors)) {
        // Handle structured errors with user-friendly messages
        const errorMessages = err.errors.map((error: any) => {
          const message = error.message || error;
          if (message.toLowerCase().includes('email already in use')) {
            return 'This email is already registered. Please use a different email or sign in.';
          } else if (message.toLowerCase().includes('phone')) {
            return 'Please check your phone number format.';
          } else if (message.toLowerCase().includes('password')) {
            return 'Please ensure your password meets the requirements.';
          } else if (message.toLowerCase().includes('username')) {
            return 'Please choose a different username.';
          }
          return message;
        });
        setError(errorMessages.join(', '));
      } else {
        const errorMessage = typeof err === "string" ? err : err?.message || "Something went wrong. Please try again.";
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      setTimeout(() => {
        setUserNameError("");
      }, 3000);
    }
  };

  const referrals = [
    { title: "Krent Customer Service", id: 1 },
    { title: "Agent", id: 2 },
    { title: "Instagram", id: 3 },
    { title: "Twitter", id: 4 },
    { title: "Facebook", id: 5 },
    { title: "Google", id: 6 },
  ];
  const dropdownOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  const associationDropdownOptions = [
    { value: "yes", label: "Yes, I am" },
    { value: "no", label: "No" },
  ];
  console.log(type)
  return (
    <OnboardingLayout>
      <div className="container">
        <h3
          className="text-md-start fw-bolder mb-2 text-link text-black mt-3"
          onClick={() => navigate("/")}
        >
          <Logo />
        </h3>

        <h4 className="fw-bolder">
          {type === "buyer"
            ? "Create a buyer account"
            : type === "developer"
              ? "Create a developer account"
              : type === "agent"
                ? "Create an agent account"
                : type === "property-manager"
                  ? "Create a property manager account"
                  : "Create a seller account"}
        </h4>

        <p className="text-secondary fs-14 mb-4">
          Signing up is easy, let’s get you on board
        </p>

        <ErrorDisplay errors={error ? [{ message: error, field: 'general' }] : []} />
        <SuccessDisplay message={success} />

        <form className="mb-children mt-4 mt-md-3" onSubmit={handleSubmit}>
          <div className="col mb-3">
            <InputComponent
              label="First Name"
              placeholder="Enter your First name"
              icon="user"
              iconType="fa"
              type="text"
              value={firstname}
              change={(e) => setFirstname(e.target.value)}
              required
            />
          </div>

          <div className="col mb-3">
            <InputComponent
              label="Last Name"
              placeholder="Enter your Last name"
              icon="user"
              iconType="fa"
              type="text"
              value={lastname}
              change={(e) => setLastname(e.target.value)}
              required
            />
          </div>

          <div className="col mb-3">
            <InputComponent
              label="User Name"
              placeholder="Enter your username"
              icon="user"
              iconType="fa"
              type="text"
              value={preferredName}
              change={(e) => {
                setPreferredName(e.target.value);
              }}
              required
            />
            {userNameError && (
              <span className="text-danger fs-12">{userNameError}</span>
            )}
          </div>
          {type === "agent" && (
            <>
              <div className="col mb-3">
                <SelectDropdown
                  label="Are you a NIESV or a Registered Agent?"
                  options={dropdownOptions}
                  icon="user"
                  iconType="fa"
                  value={selectedOption}
                  change={handleSelectChange}
                  required
                />

                {selectedOption === "yes" && (
                  <div className="mt-3">
                    <InputComponent
                      label="Enter your NIESV/agent number"
                      placeholder="Enter your NIESV/agent number"
                      icon="user"
                      iconType="fa"
                      type="text"
                      value={regNumber}
                      change={(e) => {
                        setRegNumber(e.target.value);
                        setNiesvVerified(false);
                      }}
                      required
                    />
                    {niesvError && (
                      <span className="text-danger fs-12">{niesvError}</span>
                    )}
                    {niesvVerified && (
                      <p className="text-success fs-12">Verified</p>
                    )}
                  </div>
                )}
              </div>
              <div className="col mb-3">
                <SelectDropdown
                  label="Are you an Association of Estate Agents of Nigeria member"
                  options={associationDropdownOptions}
                  icon="user"
                  iconType="fa"
                  value={isAssociationMember}
                  change={handleAssociationChange}
                  required
                />

                {isAssociationMember === "yes" && (
                  <div className="mt-3">
                    <InputComponent
                      label="Enter your Estate Agent Number"
                      placeholder="Enter your Estate Agent Number"
                      icon="user"
                      iconType="fa"
                      type="text"
                      value={estateNumber}
                      change={(e) => {
                        setEstateNumber(e.target.value);
                        setAeanVerified(false);
                      }}
                      required
                    />
                    {aeanError && (
                      <span className="text-danger fs-12">{aeanError}</span>
                    )}
                    {aeanVerified && (
                      <p className="text-success fs-12">Verified</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="col mb-3">
            <InputComponent
              label="Phone Number"
              placeholder="Enter your phone number"
              icon="Call"
              type="text"
              value={phoneNumber}
              change={(e) => setPhoneNumber(e.target.value)}
              required
              length={11}
            />
          </div>

          <div className="col mb-3">
            <InputComponent
              label="Email Address"
              placeholder="Enter your email address"
              icon="Message"
              type="email"
              value={email}
              change={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* <div className='col  mb-3'>
            <div className='row'>
              <div className='col-11'>
                <InputComponent
                  label='Password'
                  placeholder='Enter your password'
                  icon='Lock'
                  type={togglePassword ? 'text' : 'password'}
                  value={password}
                  change={(e) => setPassword(e.target.value)}
                  passwordIcon='open'
                  required
                />
              </div>
              <div className='col-1 d-flex flex-column justify-content-center align-items-center'>
                <span
                  style={{
                    marginTop: '30px',
                  }}
                  className='input-group-text input-group-text-0 '
                >
                  {togglePassword ? (
                    <i
                      className='iconly-Show icli fs-4'
                      onClick={() => setTogglePassword(!togglePassword)}
                      style={{ cursor: 'pointer', color: '#05569f' }}
                    ></i>
                  ) : (
                    <i
                      className='iconly-Hide icli fs-4'
                      onClick={() => setTogglePassword(!togglePassword)}
                      style={{ cursor: 'pointer', color: '#05569f' }}
                    ></i>
                  )}
                </span>
              </div>
            </div>
          </div> */}
          {/* test */}
          <div className="form-group col mb-3">
            <label
              className="text-uppercase fw-600 fs-12 m-1"
              htmlFor="password"
            >
              Password
            </label>
            <div className="input-group">
              <span
                className="input-group-text input-group-text-0 "
                id="basic-addon1"
              >
                <i className={`iconly-Lock icli fs-4`}></i>
              </span>

              <input
                type={togglePassword ? "text" : "password"}
                className="form-control form-control-0 ps-3 py-3 border-start-0"
                id="password"
                aria-describedby="emailHelp"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ borderRight: "none" }}
              />

              <span className="input-group-text input-group-text-0 ">
                {togglePassword ? (
                  <i
                    className="iconly-Show icli fs-4"
                    onClick={() => setTogglePassword(!togglePassword)}
                    style={{ cursor: "pointer", color: "#05569f" }}
                  ></i>
                ) : (
                  <i
                    className="iconly-Hide icli fs-4"
                    onClick={() => setTogglePassword(!togglePassword)}
                    style={{ cursor: "pointer", color: "#05569f" }}
                  ></i>
                )}
              </span>
            </div>
          </div>
          {/* referral */}
          <div className="form-group mb-4">
            <label className="text-uppercase fw-600 fs-12 m-1">
              How did you hear about Krent?
            </label>
            <div className="input-group">
              <span
                className="input-group-text input-group-text-0 "
                id="basic-addon1"
              >
                {/* <i className={`fa-light fa--user icli fs-4`}></i> */}
                <i className={`iconly-Volume-Up icli fs-4`}></i>
              </span>
              <select
                required
                className="form-control form-control-0 ps-3 py-3 border-start-0"
                defaultValue={referral}
                onChange={(e) => {
                  setReferral(e.target.value);
                }}
              >
                <option value="">Select An Option</option>
                {referrals.map((d: any) => {
                  return (
                    <option key={d.id} value={d.title}>
                      {d.title}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          {/* test */}
          {type !== "developer" && (
            <div className="col mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="terms"
                  checked={agree}
                  id="terms"
                  onChange={() => setAgree(!agree)}
                  required
                />
                <label
                  className="form-check-label fs-14 fw-400"
                  htmlFor="terms"
                >
                  By signing up you agree to our Terms of service and privacy
                  policy.
                </label>
              </div>
            </div>
          )}

          <div className="d-grid">
            <PrimaryButton
              type="primary"
              text={type === "developer" ? "Save and continue" : "Sign up"}
              loading={loading}
            />
          </div>
        </form>

        <p className="text-secondary text-center my-4 my-md-3 fs-14">
          Already have an account?{" "}
          <Link className="text-link" to="/sign-in">
            Sign In
          </Link>
        </p>
      </div>
    </OnboardingLayout>
  );
};
